import api from '@/api';
import { FilterInfo, PostSurveyResponse, SurveyHistoryData } from '@/types';
import { checkRequestState, parseModelResponse } from '@/utils/survey/common';
import { STREAMING_DATA_TYPES } from '@/utils/survey/constants';

export default class SurveyGenerator {
  /**
   * 要約生成を開始する関数
   */
  static async generate(params: {
    sessionId: string;
    question: string;
    filterInfo: FilterInfo;
    cached_expanded_queries: Array<string>;
    deepMode: boolean;
    history: SurveyHistoryData;
    groupId?: number;
    groupTags?: Array<number>;
    signal?: AbortSignal;
  }) {
    for await (const chunk of api.postSurvey(
      params.sessionId,
      params.question,
      params.filterInfo.searchScope,
      params.filterInfo.docTypes,
      params.cached_expanded_queries,
      params.filterInfo.specifiedFiles,
      params.deepMode,
      params.filterInfo.groupId,
      params.filterInfo.groupTags,
      params.signal,
    )) {
      if (chunk === undefined) continue;
      this.handleChunkToHistory(params.history, chunk);
    }
  }

  /**
   * 他画面に遷移した後にsurvey実行中の画面に戻った際に、再接続する関数
   */
  static async reconnectSurveyStream(params: {
    sessionId: string;
    requestId: string;
    history: SurveyHistoryData;
    signal?: AbortSignal;
  }) {
    for await (const chunk of api.reconnectPostSurvey(
      params.sessionId,
      params.requestId,
      params.signal,
    )) {
      this.handleChunkToHistory(params.history, chunk);
    }
  }

  /**
   * エラーや停止したブロックの再生成を行う関数
   */
  static async regenerate(params: {
    sessionId: string;
    requestId: string;
    history: SurveyHistoryData;
    signal?: AbortSignal;
  }) {
    for await (const chunk of api.regeneratePostSurvey(
      params.sessionId,
      params.requestId,
      params.signal,
    )) {
      if (chunk === undefined) continue;
      this.handleChunkToHistory(params.history, chunk);
    }
  }

  /**
   * チャンクデータをSurveyHistoryDataに反映するstaticユーティリティ
   */
  static handleChunkToHistory(
    history: SurveyHistoryData,
    chunk: PostSurveyResponse,
  ) {
    switch (chunk.type) {
      case STREAMING_DATA_TYPES.META:
        if (chunk.key === 'survey_request_id') history.requestId = chunk.value;
        if (chunk.key === 'state') {
          history.requestState = chunk.value;
          if (chunk.value === 'aborted') {
            history.status.isError = true;
          }
        }
        break;
      case STREAMING_DATA_TYPES.ERROR:
        history.status.isError = true;
        history.status.errorCause = chunk.message;
        break;
      case STREAMING_DATA_TYPES.PROGRESS:
        history.status.progressMessage = chunk.message;
        break;
      // ディープリサーチのプロセス (推論結果と検索クエリ)
      case STREAMING_DATA_TYPES.DEEP_MODE_PROCESS:
        if (history.status.progressMessage !== '') {
          history.status.progressMessage = '';
        }
        if (Array.isArray(history.deepModeProcesses)) {
          history.deepModeProcesses = [
            ...history.deepModeProcesses,
            { message_type: chunk.message_type, message: chunk.message },
          ];
        }
        break;
      // 検索結果が0件
      case STREAMING_DATA_TYPES.NO_HIT_SOURCES:
        history.status.isNoHit = true;

        if (history.requestId !== chunk.request_id) {
          history.requestId = chunk.request_id;
        }
        break;
      case STREAMING_DATA_TYPES.SOURCES_DONE:
        break;
      // ソース取得
      case STREAMING_DATA_TYPES.SOURCE:
        if (Array.isArray(history.sources)) {
          history.sources = [...history.sources, chunk];
        }
        break;
      // Chain Of Thought の生成開始
      case STREAMING_DATA_TYPES.REASONING_START:
        history.status.progressMessage =
          '回答を生成するための推論を立てています';
        break;
      // Chain Of Thought の生成内容
      case STREAMING_DATA_TYPES.REASONING_TOKEN:
        history.originMarkdownReasoning =
          (history.originMarkdownReasoning || '') + chunk.content;
        history.reasoning = parseModelResponse(
          history.originMarkdownReasoning,
          history.sources,
          true,
        );
        break;
      // Chain Of Thought の生成完了
      case STREAMING_DATA_TYPES.REASONING_END:
        if (history.status.progressMessage !== '') {
          history.status.progressMessage = '';
        }
        break;
      // 要約本文
      case STREAMING_DATA_TYPES.TOKEN:
        if (history.status.progressMessage !== '') {
          history.status.progressMessage = '';
        }
        if (!history.requestId || history.requestId !== chunk.request_id) {
          history.requestId = chunk.request_id;
        }

        history.originMarkdownSummary =
          (history.originMarkdownSummary || '') + chunk.content;
        history.summary = parseModelResponse(
          history.originMarkdownSummary,
          history.sources,
          true,
        );
        break;
      // 要約完了
      case STREAMING_DATA_TYPES.SURVEY_DONE: {
        history.status.isInProgress = false;
        history.status.progressMessage = '';

        if (
          history.deepMode &&
          checkRequestState(
            history.requestState,
            ['stopping', 'stopped', 'aborted'],
            false,
          )
        ) {
          history.requestState = undefined;
        }
        break;
      }
      // 関連質問
      case STREAMING_DATA_TYPES.RELATED_QUESTIONS:
        history.relatedQuestions = chunk.related_questions;
        break;
      default:
        break;
    }
  }
}
